import { getStageConfig, getOrderedStages } from "@/data/workflowEngine";
import { TOOLTIP_STYLE, GRID_STROKE } from "@/lib/chartTheme";
import { useAppData } from "@/data/AppDataContext";
import { SalesIntelligenceReport } from "@/components/dashboards/SalesIntelligenceReport";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { Link } from "react-router-dom";
import { getBlockerTags } from "@/data/workflowBlockers";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";
import {
  Briefcase, ShieldAlert, Shield, Package, Users, ClipboardCheck,
  CheckCircle, Clock, Send, ArrowRight, AlertTriangle, XCircle,
  Star, TrendingUp, Wrench, Building2, Zap, DollarSign, Megaphone, BarChart3,
  Download, Receipt, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { JobStage } from "@/data/types";
import { getReadinessScore } from "@/components/JobWorkflowStatus";
import { useCrewUtilization } from "@/hooks/useCrewUtilization";
import { Progress } from "@/components/ui/progress";
import { useContractorScorecards } from "@/hooks/useContractorScorecards";
import { ContractorScorecardTable } from "@/components/ContractorScorecard";
import { useState, useMemo } from "react";
import { BranchRegionFilter, EMPTY_BR, filterByBranchRegion, type BranchRegionState } from "@/components/BranchRegionFilter";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";
import type { Job } from "@/data/types";
import { REPORTS_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import { isReportsServerPagedRoute, useBrowserLocationSnapshot } from "@/lib/routeScope";
import { useReportsServerData, type ReportsServerDataResult } from "@/hooks/useReportsServerData";

function getJobDisplayRef(job: Pick<Job, "id" | "jobNumber">): string {
  const jobNumber = typeof job.jobNumber === "string" ? job.jobNumber.trim() : "";
  return jobNumber || job.id;
}

const CHART_COLORS = {
  primary: "hsl(var(--chart-1))",
  accent: "hsl(var(--chart-3))",
  success: "hsl(var(--chart-2))",
  warning: "hsl(var(--chart-5))",
  destructive: "hsl(var(--destructive))",
  info: "hsl(var(--info))",
  muted: "hsl(var(--muted-foreground))",
};

const SEVERITY_COLORS: Record<string, string> = {
  Urgent: CHART_COLORS.destructive,
  Medium: CHART_COLORS.warning,
  Low: CHART_COLORS.info,
};

const REPORTS_LIST_PAGE_SIZE = 10;

type ListPaginationKey =
  | "issueSourceData"
  | "issuesByAssignee"
  | "issuesByProjectType"
  | "crewMetrics"
  | "scorecards"
  | "internalJobs"
  | "subJobs"
  | "unassignedJobs"
  | "permitBlocked"
  | "materialsBlocked"
  | "qaJobs"
  | "closeoutJobs"
  | "bottlenecks"
  | "lowestReadiness"
  | "campaignPerf"
  | "creditsByType"
  | "creditsByCustomer"
  | "creditsByAttribution"
  | "creditsAccountability";

function ListPagination({
  page,
  totalPages,
  onChange,
  label,
}: {
  page: number;
  totalPages: number;
  onChange: (next: number) => void;
  label?: string;
}) {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-end gap-2 pt-2">
      {label ? <span className="text-[10px] text-muted-foreground">{label}</span> : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-6 px-2"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </Button>
      <span className="text-[10px] text-muted-foreground">{page}/{totalPages}</span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-6 px-2"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

const RESPONSE_COLORS: Record<string, string> = {
  Accepted: CHART_COLORS.success,
  "Bid Submitted": CHART_COLORS.info,
  Pending: CHART_COLORS.warning,
  "No Response": CHART_COLORS.muted,
  Rejected: CHART_COLORS.destructive,
  Viewed: CHART_COLORS.accent,
};

function SectionHeader({ icon: Icon, label }: { icon: React.ElementType; label: string }) {
  return (
    <div className="flex items-center gap-2 pb-1 mb-3 border-b">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">{label}</h2>
    </div>
  );
}

function downloadCSV(filename: string, headers: string[], rows: (string | number)[][]) {
  const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${c}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Reports({
  hideHeader = false,
  serverDataOverride,
}: { hideHeader?: boolean; serverDataOverride?: ReportsServerDataResult } = {}) {
  const appData = useAppData();
  const location = useBrowserLocationSnapshot();
  const reportsServerEnabled = REPORTS_SERVER_PAGING_ENABLED && isReportsServerPagedRoute(location.pathname);
  const ownedServerData = useReportsServerData({ enabled: reportsServerEnabled && !serverDataOverride });
  const serverData = serverDataOverride ?? ownedServerData;
  const jobs = reportsServerEnabled ? serverData.jobs : appData.jobs;
  const issues = reportsServerEnabled ? serverData.issues : appData.issues;
  const opportunities = reportsServerEnabled ? serverData.opportunities : appData.opportunities;
  const contractors = reportsServerEnabled ? serverData.contractors : appData.contractors;
  const crews = useMemo(() => reportsServerEnabled ? [] : appData.crews, [reportsServerEnabled, appData.crews]);
  const credits = reportsServerEnabled ? serverData.credits : appData.credits;
  const buildReviewEvents = reportsServerEnabled ? serverData.buildReviewEvents : appData.buildReviewEvents;
  const salesQuotes = reportsServerEnabled ? serverData.quotes : appData.quotes;
  const [brFilter, setBrFilter] = useState<BranchRegionState>(EMPTY_BR);
  const fin$ = useFinancialPermissions();
  const [listPages, setListPages] = useState<Record<ListPaginationKey, number>>({
    issueSourceData: 1,
    issuesByAssignee: 1,
    issuesByProjectType: 1,
    crewMetrics: 1,
    scorecards: 1,
    internalJobs: 1,
    subJobs: 1,
    unassignedJobs: 1,
    permitBlocked: 1,
    materialsBlocked: 1,
    qaJobs: 1,
    closeoutJobs: 1,
    bottlenecks: 1,
    lowestReadiness: 1,
    campaignPerf: 1,
    creditsByType: 1,
    creditsByCustomer: 1,
    creditsByAttribution: 1,
    creditsAccountability: 1,
  });

  const setListPage = (key: ListPaginationKey, page: number) =>
    setListPages((prev) => ({ ...prev, [key]: Math.max(1, page) }));
  const paginate = <T,>(items: T[], key: ListPaginationKey) => {
    const totalPages = Math.max(1, Math.ceil(items.length / REPORTS_LIST_PAGE_SIZE));
    const currentPage = Math.min(Math.max(1, listPages[key] ?? 1), totalPages);
    const start = (currentPage - 1) * REPORTS_LIST_PAGE_SIZE;
    return {
      page: currentPage,
      totalPages,
      items: items.slice(start, start + REPORTS_LIST_PAGE_SIZE),
    };
  };

  const filteredJobs = filterByBranchRegion(jobs, brFilter);
  const filteredIssues = brFilter.branch === "all" && brFilter.region === "all"
    ? issues
    : issues.filter((i) => {
        const job = jobs.find((j) => j.id === i.jobId);
        // Strict scoping: if an issue cannot be tied to a job, do not include it
        // in a branch/region filtered view to avoid cross-scope leakage.
        if (!job) return false;
        if (brFilter.branch !== "all" && job.branch !== brFilter.branch) return false;
        if (brFilter.region !== "all" && job.region !== brFilter.region) return false;
        return true;
      });

  const scorecards = useContractorScorecards(contractors, opportunities, filteredJobs, filteredIssues, credits);
  const repNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of crews) map.set(c.id, c.name);
    for (const c of contractors) map.set(c.id, c.companyName);
    return map;
  }, [crews, contractors]);
  const crewMetrics = useCrewUtilization(filteredJobs, filteredIssues, undefined, repNameById);

  if (fin$.isCrew) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
        <div className="h-16 w-16 rounded-2xl bg-destructive/10 flex items-center justify-center mb-4">
          <ShieldAlert className="h-8 w-8 text-destructive" />
        </div>
        <h1 className="text-xl font-bold mb-2">Access Restricted</h1>
        <p className="text-sm text-muted-foreground max-w-md">
          Reports contain internal operational data not available for your role.
          Contact your administrator if you need access.
        </p>
      </div>
    );
  }

  if (reportsServerEnabled && serverData.loading && jobs.length === 0) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center rounded-xl border bg-muted/20 p-8 text-sm text-muted-foreground">
        Loading report intelligence...
      </div>
    );
  }

  if (reportsServerEnabled && serverData.error && jobs.length === 0) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-sm">
        <p className="font-semibold text-destructive">Unable to load server-backed reports.</p>
        <p className="mt-1 text-muted-foreground">Apply the Reports SQL views, then refresh this route.</p>
      </div>
    );
  }

  const isCompleted = (j: typeof filteredJobs[0]) => (getStageConfig(j.currentStage)?.order ?? 0) >= 100;
  const openJobs = filteredJobs.filter((j) => !isCompleted(j));
  const completedJobs = filteredJobs.filter(isCompleted);
  const openIssues = filteredIssues.filter((i) => i.status === "Open" || i.status === "In Progress");
  const urgentIssues = openIssues.filter((i) => i.severity === "Urgent");

  // ─── Stage pipeline (from workflow engine) ───
  const stageCounts = getOrderedStages(false).map((stage) => ({
    stage: stage.label.length > 18 ? stage.label.substring(0, 16) + "…" : stage.label,
    fullStage: stage.id,
    count: filteredJobs.filter((j) => j.currentStage === stage.id).length,
  })).filter((s) => s.count > 0);

  // ─── Issues by severity & category ───
  const severityData = ["Urgent", "Medium", "Low"].map((sev) => ({
    name: sev,
    count: openIssues.filter((i) => i.severity === sev).length,
  })).filter((s) => s.count > 0);

  const issueCategories = Array.from(new Set(filteredIssues.map((i) => i.issueType)));
  const issueCategoryData = issueCategories.map((cat) => ({
    category: cat.replace(" Issue", ""),
    open: filteredIssues.filter((i) => i.issueType === cat && (i.status === "Open" || i.status === "In Progress")).length,
    resolved: filteredIssues.filter((i) => i.issueType === cat && (i.status === "Resolved" || i.status === "Closed")).length,
  })).sort((a, b) => b.open - a.open);

  // ─── Issue source analysis ───
  const issueSources = Array.from(new Set(filteredIssues.map((i) => i.source)));
  const issueSourceData = issueSources.map((src) => ({
    source: src,
    open: filteredIssues.filter((i) => i.source === src && (i.status === "Open" || i.status === "In Progress")).length,
    resolved: filteredIssues.filter((i) => i.source === src && (i.status === "Resolved" || i.status === "Closed")).length,
    total: filteredIssues.filter((i) => i.source === src).length,
  })).sort((a, b) => b.total - a.total);

  // ─── Issues by crew / sub ───
  const issuesByAssignee = (() => {
    const map = new Map<string, { name: string; type: string; open: number; resolved: number }>();
    filteredIssues.forEach((issue) => {
      const job = filteredJobs.find((j) => j.id === issue.jobId);
      if (!job) return;
      const assignee = job.assignmentType === "Subcontractor" ? (job.assignedSubcontractor || "Unassigned Sub") :
                        job.assignmentType === "Internal Crew" ? (job.assignedCrew || "Unassigned Crew") : "Unassigned";
      if (!map.has(assignee)) map.set(assignee, { name: assignee, type: job.assignmentType, open: 0, resolved: 0 });
      const entry = map.get(assignee)!;
      if (issue.status === "Open" || issue.status === "In Progress") entry.open++;
      else entry.resolved++;
    });
    return [...map.values()].sort((a, b) => b.open - a.open);
  })();

  // ─── Issues by project type ───
  const issuesByProjectType = (() => {
    const map = new Map<string, { open: number; resolved: number }>();
    filteredIssues.forEach((issue) => {
      const job = filteredJobs.find((j) => j.id === issue.jobId);
      if (!job) return;
      if (!map.has(job.projectType)) map.set(job.projectType, { open: 0, resolved: 0 });
      const entry = map.get(job.projectType)!;
      if (issue.status === "Open" || issue.status === "In Progress") entry.open++;
      else entry.resolved++;
    });
    return [...map.entries()].map(([type, data]) => ({ type, ...data })).sort((a, b) => b.open - a.open);
  })();

  // ─── Unresolved issues by age ───
  const today = new Date();
  const issueAgeData = (() => {
    const buckets = [
      { label: "< 3 days", min: 0, max: 3, count: 0 },
      { label: "3-7 days", min: 3, max: 7, count: 0 },
      { label: "7-14 days", min: 7, max: 14, count: 0 },
      { label: "14+ days", min: 14, max: Infinity, count: 0 },
    ];
    openIssues.forEach((i) => {
      const age = Math.floor((today.getTime() - new Date(i.dateCreated).getTime()) / (1000 * 60 * 60 * 24));
      const bucket = buckets.find((b) => age >= b.min && age < b.max);
      if (bucket) bucket.count++;
    });
    return buckets;
  })();

  // ─── Blocker analysis ───
  const permitBlocked = openJobs.filter((j) => j.permitRequired && (j.permitStatus === "Submitted" || j.permitStatus === "Revision Needed"));
  const materialsBlocked = openJobs.filter((j) => j.materialReadiness === "Missing Items" || (j.materialReadiness === "Not Ordered" && (getStageConfig(j.currentStage)?.order ?? 0) > 25));
  const qaJobs = filteredJobs.filter((j) => (getStageConfig(j.currentStage)?.order ?? 0) === 90);
  const readyDispatch = openJobs.filter((j) => (getStageConfig(j.currentStage)?.order ?? 0) === 65 || j.dispatchStatus === "Ready for Dispatch");
  const awaitingSub = openJobs.filter((j) => j.dispatchStatus === "Awaiting Response" || j.dispatchStatus === "Sent to Contractor Network");

  const closeoutJobs = completedJobs.filter((j) => {
    const jobIssues = filteredIssues.filter((i) => i.jobId === j.id && (i.status === "Open" || i.status === "In Progress"));
    return jobIssues.length > 0 || !j.qaSubmitted;
  });

  // ─── Assignment breakdown ───
  const internalJobs = openJobs.filter((j) => j.assignmentType === "Internal Crew");
  const subJobs = openJobs.filter((j) => j.assignmentType === "Subcontractor");
  const unassignedJobs = openJobs.filter((j) => j.assignmentType === "Unassigned");

  // ─── Subcontractor response analysis ───
  const responseStatuses = ["Accepted", "Bid Submitted", "Viewed", "Pending", "No Response", "Rejected"];
  const responseData = responseStatuses.map((status) => ({
    name: status,
    count: opportunities.filter((o) => o.responseStatus === status).length,
  })).filter((s) => s.count > 0);

  // ─── Contractor scorecards ───
  const activeContractors = contractors.filter((c) => c.active);
  const contractorScores = activeContractors.map((c) => {
    const cOpps = opportunities.filter((o) => o.contractorId === c.id);
    const accepted = cOpps.filter((o) => o.responseStatus === "Accepted").length;
    const rejected = cOpps.filter((o) => o.responseStatus === "Rejected").length;
    const bids = cOpps.filter((o) => o.responseStatus === "Bid Submitted").length;
    const responded = accepted + rejected + bids;
    const responseRate = cOpps.length > 0 ? Math.round((responded / cOpps.length) * 100) : 0;
    const acceptRate = responded > 0 ? Math.round(((accepted + bids) / responded) * 100) : 0;
    const wonJobs = cOpps.filter((o) => o.isWinner).length;
    const jobIssues = issues.filter((i) => {
      const jobOpp = cOpps.find((o) => o.jobId === i.jobId && o.isWinner);
      return jobOpp && (i.status === "Open" || i.status === "In Progress");
    });
    return {
      ...c,
      totalOpps: cOpps.length,
      accepted,
      rejected,
      bids,
      responseRate,
      acceptRate,
      wonJobs,
      openIssues: jobIssues.length,
    };
  }).sort((a, b) => b.responseRate - a.responseRate);

  const totalPipeline = openJobs.reduce((sum, j) => sum + getJobDisplayValue(j), 0);
  const completedValue = completedJobs.reduce((sum, j) => sum + getJobDisplayValue(j), 0);

  // ─── Financial metrics ───
  const totalRevenue = filteredJobs.reduce((s, j) => s + getJobDisplayValue(j), 0);
  const totalMargin = filteredJobs.reduce((s, j) => s + (j.financials?.marginEstimate ?? 0), 0);
  const avgMarginPct = filteredJobs.filter((j) => j.financials).length > 0
    ? Math.round(filteredJobs.filter((j) => j.financials).reduce((s, j) => {
        const cv = j.financials!.contractValue || 1;
        return s + (j.financials!.marginEstimate / cv) * 100;
      }, 0) / filteredJobs.filter((j) => j.financials).length)
    : 0;

  // ─── Revenue by lead source ───
  const revenueBySource = (() => {
    const map = new Map<string, { revenue: number; jobs: number; margin: number }>();
    filteredJobs.forEach((j) => {
      const src = j.marketing?.leadSource || "Unknown";
      const existing = map.get(src) || { revenue: 0, jobs: 0, margin: 0 };
      existing.revenue += getJobDisplayValue(j);
      existing.jobs++;
      existing.margin += j.financials?.marginEstimate ?? 0;
      map.set(src, existing);
    });
    return [...map.entries()].map(([source, d]) => ({ source, ...d })).sort((a, b) => b.revenue - a.revenue);
  })();

  // ─── Revenue over time (by month) ───
  const revenueOverTime = (() => {
    const map = new Map<string, { revenue: number; margin: number; jobs: number }>();
    filteredJobs.forEach((j) => {
      const date = j.scheduledInstallDate || j.targetInstallDate;
      if (!date) return;
      const month = date.substring(0, 7); // YYYY-MM
      const existing = map.get(month) || { revenue: 0, margin: 0, jobs: 0 };
      existing.revenue += getJobDisplayValue(j);
      existing.margin += j.financials?.marginEstimate ?? 0;
      existing.jobs++;
      map.set(month, existing);
    });
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, d]) => ({ month: new Date(month + "-01").toLocaleDateString("en-US", { month: "short", year: "2-digit" }), ...d }));
  })();

  // ─── Campaign performance ───
  const campaignPerf = (() => {
    const map = new Map<string, { revenue: number; jobs: number; closed: number }>();
    filteredJobs.filter((j) => j.marketing?.campaignName).forEach((j) => {
      const name = j.marketing!.campaignName;
      const existing = map.get(name) || { revenue: 0, jobs: 0, closed: 0 };
      existing.revenue += getJobDisplayValue(j);
      existing.jobs++;
      if (isCompleted(j)) existing.closed++;
      map.set(name, existing);
    });
    return [...map.entries()].map(([campaign, d]) => ({
      campaign: campaign.length > 20 ? campaign.substring(0, 18) + "…" : campaign,
      ...d,
      closeRate: d.jobs > 0 ? Math.round((d.closed / d.jobs) * 100) : 0,
    })).sort((a, b) => b.revenue - a.revenue);
  })();

  // ─── Install readiness distribution ───
  const readinessScores = openJobs.map((j) => {
    const oi = filteredIssues.filter((i) => i.jobId === j.id && (i.status === "Open" || i.status === "In Progress"));
    return { job: j, score: getReadinessScore(j, oi, buildReviewEvents) };
  });
  const readyCount = readinessScores.filter((r) => r.score === 100).length;
  const almostCount = readinessScores.filter((r) => r.score >= 60 && r.score < 100).length;
  const blockedCount = readinessScores.filter((r) => r.score < 60).length;
  const lowestReadiness = [...readinessScores].sort((a, b) => a.score - b.score);
  const lowestReadinessJobs = lowestReadiness.filter((r) => r.score < 100);

  // ─── Bottleneck summary ───
  const bottlenecks = [
    { label: "Permit Holds", count: permitBlocked.length, icon: Shield, color: "text-warning", bg: "bg-warning/10 border-warning/20" },
    { label: "Materials Blocked", count: materialsBlocked.length, icon: Package, color: "text-warning", bg: "bg-warning/10 border-warning/20" },
    { label: "Awaiting Sub Response", count: awaitingSub.length, icon: Send, color: "text-accent", bg: "bg-accent/10 border-accent/20" },
    { label: "QA Review", count: qaJobs.length, icon: ClipboardCheck, color: "text-info", bg: "bg-info/10 border-info/20" },
    { label: "Ready for Dispatch", count: readyDispatch.length, icon: Wrench, color: "text-primary", bg: "bg-primary/10 border-primary/20" },
    { label: "Unassigned", count: unassignedJobs.length, icon: Users, color: "text-muted-foreground", bg: "bg-muted border-border" },
    { label: "Closeout Needed", count: closeoutJobs.length, icon: CheckCircle, color: "text-success", bg: "bg-success/10 border-success/20" },
    { label: "Urgent Issues", count: urgentIssues.length, icon: AlertTriangle, color: "text-destructive", bg: "bg-destructive/10 border-destructive/20" },
  ].filter((b) => b.count > 0);

  const bottlenecksPageData = paginate(bottlenecks, "bottlenecks");
  const lowestReadinessPageData = paginate(lowestReadinessJobs, "lowestReadiness");

  const issueSourcePageData = paginate(issueSourceData, "issueSourceData");
  const issuesByAssigneePageData = paginate(issuesByAssignee, "issuesByAssignee");
  const issuesByProjectTypePageData = paginate(issuesByProjectType, "issuesByProjectType");
  const crewMetricsPageData = paginate(crewMetrics, "crewMetrics");
  const internalJobsPageData = paginate(internalJobs, "internalJobs");
  const subJobsPageData = paginate(subJobs, "subJobs");
  const unassignedJobsPageData = paginate(unassignedJobs, "unassignedJobs");
  const permitBlockedPageData = paginate(permitBlocked, "permitBlocked");
  const materialsBlockedPageData = paginate(materialsBlocked, "materialsBlocked");
  const qaJobsPageData = paginate(qaJobs, "qaJobs");
  const closeoutJobsPageData = paginate(closeoutJobs, "closeoutJobs");
  const campaignPerfPageData = paginate(campaignPerf, "campaignPerf");
  const scorecardsPageData = paginate(scorecards, "scorecards");
  const creditsByType = Array.from(
    new Set(
      credits.map((c) => c.creditType).filter((creditType): creditType is string => Boolean(creditType)),
    ),
  ).map((creditType) => {
    const creditTypeCredits = credits.filter((c) => c.creditType === creditType);
    const total = creditTypeCredits.reduce((s, c) => s + c.creditAmount, 0);
    return { creditType, total, count: creditTypeCredits.length };
  });
  const creditsByTypePageData = paginate(creditsByType, "creditsByType");

  const creditsByCustomer = Array.from(new Set(credits.map((c) => c.customerId))).map((cust) => {
    const customerCredits = credits.filter((c) => c.customerId === cust);
    const total = customerCredits.reduce((s, c) => s + c.creditAmount, 0);
    return { customerId: cust, total, count: customerCredits.length };
  });
  const creditsByAttribution = Array.from(new Set(credits.map((c) => c.attributionType))).map((attributionType) => {
    const attributionCredits = credits.filter((c) => c.attributionType === attributionType);
    const total = attributionCredits.reduce((s, c) => s + c.creditAmount, 0);
    return { attributionType, total, count: attributionCredits.length };
  });
  const creditsAccountability = (() => {
    const rows = Array.from(new Set(credits.filter((c) => c.relatedCrewName).map((c) => c.relatedCrewName!))).map((crew) => {
      const crewCredits = credits.filter((c) => c.relatedCrewName === crew);
      const total = crewCredits.reduce((s, c) => s + c.creditAmount, 0);
      const crewJobs = filteredJobs.filter((j) => j.assignedCrew === crew);
      const avgPerJob = crewJobs.length > 0 ? Math.round(total / crewJobs.length) : total;
      const topReasons = Array.from(new Set(crewCredits.map((c) => c.reasonCategory))).slice(0, 2);
      return {
        entity: crew,
        entityType: "Crew",
        credits: crewCredits.length,
        total,
        avgPerJob,
        patterns: topReasons.join(", "),
        crewId: null,
      };
    });

    const contractorRows = Array.from(new Set(credits.filter((c) => c.relatedContractorId).map((c) => c.relatedContractorId!))).map((conId) => {
      const con = contractors.find((c) => c.id === conId);
      const conCredits = credits.filter((c) => c.relatedContractorId === conId);
      const total = conCredits.reduce((s, c) => s + c.creditAmount, 0);
      const conJobs = filteredJobs.filter((j) => j.assignedSubcontractor === con?.companyName);
      const avgPerJob = conJobs.length > 0 ? Math.round(total / conJobs.length) : total;
      const topReasons = Array.from(new Set(conCredits.map((c) => c.reasonCategory))).slice(0, 2);
      return {
        entity: con?.companyName || conId,
        entityType: "Sub",
        credits: conCredits.length,
        total,
        avgPerJob,
        patterns: topReasons.join(", "),
        crewId: conId,
        isContractor: true,
      };
    });

    return [...rows, ...contractorRows];
  })();
  const creditsByCustomerPageData = paginate(creditsByCustomer, "creditsByCustomer");
  const creditsByAttributionPageData = paginate(creditsByAttribution, "creditsByAttribution");
  const creditsAccountabilityPageData = paginate(creditsAccountability, "creditsAccountability");

  return (
    <div className="space-y-6 max-w-6xl">
      {hideHeader ? (
        <div className="flex justify-end">
          <BranchRegionFilter value={brFilter} onChange={setBrFilter} jobs={jobs} />
        </div>
      ) : (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-lg font-bold tracking-tight">Operational Intelligence</h1>
            <p className="text-xs text-muted-foreground">
              {filteredJobs.length} total jobs · ${totalPipeline.toLocaleString()} active pipeline · ${completedValue.toLocaleString()} completed
            </p>
          </div>
          <BranchRegionFilter value={brFilter} onChange={setBrFilter} jobs={jobs} />
        </div>
      )}

      {/* ═══ TOP KPIs ═══ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {[
          { label: "Active Jobs", value: openJobs.length, icon: Briefcase, color: "text-primary" },
          { label: "Completed", value: completedJobs.length, icon: CheckCircle, color: "text-success" },
          { label: "Open Issues", value: openIssues.length, icon: ShieldAlert, color: openIssues.length > 0 ? "text-destructive" : "text-muted-foreground" },
          { label: "Urgent", value: urgentIssues.length, icon: AlertTriangle, color: urgentIssues.length > 0 ? "text-destructive" : "text-muted-foreground" },
          { label: "Permit Hold", value: permitBlocked.length, icon: Shield, color: permitBlocked.length > 0 ? "text-warning" : "text-muted-foreground" },
          { label: "Materials", value: materialsBlocked.length, icon: Package, color: materialsBlocked.length > 0 ? "text-warning" : "text-muted-foreground" },
          { label: "QA Review", value: qaJobs.length, icon: ClipboardCheck, color: qaJobs.length > 0 ? "text-warning" : "text-muted-foreground" },
          { label: "Internal / Sub", value: `${internalJobs.length}/${subJobs.length}`, icon: Users, color: "text-info" },
        ].map((s) => (
          <Card key={s.label} className="shadow-none">
            <CardContent className="p-2.5 text-center">
              <s.icon className={`h-4 w-4 mx-auto mb-1 ${s.color}`} />
              <p className="text-lg font-bold leading-none">{s.value}</p>
              <p className="text-[9px] text-muted-foreground mt-1 font-medium uppercase tracking-wider">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ═══ SALES INTELLIGENCE ═══ */}
      <SalesIntelligenceReport quotes={reportsServerEnabled ? salesQuotes : undefined} />

      {/* ═══ BOTTLENECK ANALYSIS ═══ */}
      {bottlenecks.length > 0 && (
        <Card className="shadow-none">
          <CardContent className="p-4">
            <SectionHeader icon={Zap} label="Where Jobs Are Getting Stuck" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {bottlenecksPageData.items.map((b) => (
                <div key={b.label} className={`rounded-xl border p-3 ${b.bg}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <b.icon className={`h-4 w-4 ${b.color}`} />
                    <span className="text-2xl font-bold leading-none">{b.count}</span>
                  </div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{b.label}</p>
                </div>
              ))}
            </div>
            <ListPagination
              page={bottlenecksPageData.page}
              totalPages={bottlenecksPageData.totalPages}
              onChange={(next) => setListPage("bottlenecks", next)}
            />
          </CardContent>
        </Card>
      )}

      {/* ═══ INSTALL READINESS DISTRIBUTION ═══ */}
      <Card className="shadow-none">
        <CardContent className="p-4">
          <SectionHeader icon={CheckCircle} label="Install Readiness Overview" />
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="rounded-xl border border-success/20 bg-success/5 p-3 text-center">
              <p className="text-2xl font-bold text-success">{readyCount}</p>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ready (100%)</p>
            </div>
            <div className="rounded-xl border border-warning/20 bg-warning/5 p-3 text-center">
              <p className="text-2xl font-bold text-warning">{almostCount}</p>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Almost (60-99%)</p>
            </div>
            <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-3 text-center">
              <p className="text-2xl font-bold text-destructive">{blockedCount}</p>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Blocked (&lt;60%)</p>
            </div>
          </div>
          {lowestReadinessJobs.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.12em] mb-2">Lowest Readiness Jobs</p>
              <div className="space-y-1.5">
                {lowestReadinessPageData.items.map((r) => {
                  const oi = filteredIssues.filter((i) => i.jobId === r.job.id && (i.status === "Open" || i.status === "In Progress"));
                  const tags = getBlockerTags(r.job, oi);
                  return (
                    <div key={r.job.id} className="flex items-center gap-3 px-3 py-2 rounded-md border bg-muted/20">
                      <Link to={`/jobs/${r.job.id}`} className="text-xs text-primary font-medium hover:underline shrink-0 w-20">
  {getJobDisplayRef(r.job)}
</Link>
                      <span className="text-xs text-muted-foreground truncate flex-1">{r.job.customerName}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <div className="w-16 h-1.5 rounded-full bg-secondary overflow-hidden">
                          <div className={`h-full rounded-full ${r.score >= 60 ? "bg-warning" : "bg-destructive"}`} style={{ width: `${r.score}%` }} />
                        </div>
                        <span className={`text-[10px] font-bold w-8 text-right ${r.score >= 60 ? "text-warning" : "text-destructive"}`}>{r.score}%</span>
                      </div>
                      <div className="flex flex-wrap gap-1 shrink-0">
                        {tags.slice(0, 2).map((t) => (
                          <span key={t.label} className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-medium border ${
                            t.severity === "critical" ? "bg-destructive/10 text-destructive border-destructive/20" : "bg-warning/10 text-warning border-warning/20"
                          }`}>{t.label}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
                <ListPagination
                  page={lowestReadinessPageData.page}
                  totalPages={lowestReadinessPageData.totalPages}
                  onChange={(next) => setListPage("lowestReadiness", next)}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══ CHARTS ROW ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Jobs by Stage */}
        <Card className="shadow-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="text-sm font-semibold">Job Pipeline by Stage</CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-4">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stageCounts} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={GRID_STROKE} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="stage" width={120} tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value: number, _name: string, props: { payload: { fullStage: string } }) => [value, props.payload.fullStage]}
                />
                <Bar dataKey="count" fill={CHART_COLORS.primary} radius={[0, 4, 4, 0]} barSize={18} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Issue Severity + Category */}
        <div className="space-y-4">
          <Card className="shadow-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-sm font-semibold">Issues by Severity</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {severityData.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
                  <CheckCircle className="h-4 w-4 mr-2 text-success" /> No open issues
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={120}>
                  <PieChart>
                    <Pie data={severityData} cx="50%" cy="50%" innerRadius={30} outerRadius={50} dataKey="count" nameKey="name" paddingAngle={3}>
                      {severityData.map((entry) => (
                        <Cell key={entry.name} fill={SEVERITY_COLORS[entry.name] || CHART_COLORS.muted} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-sm font-semibold">Issues by Category</CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-4">
              {issueCategoryData.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No issues recorded</p>
              ) : (
                <ResponsiveContainer width="100%" height={140}>
                  <BarChart data={issueCategoryData} margin={{ left: 4, right: 16, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                    <XAxis dataKey="category" tick={{ fontSize: 9 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Bar dataKey="open" name="Open" fill={CHART_COLORS.destructive} radius={[4, 4, 0, 0]} barSize={14} />
                    <Bar dataKey="resolved" name="Resolved" fill={CHART_COLORS.success} radius={[4, 4, 0, 0]} barSize={14} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ═══ ISSUE INTELLIGENCE ═══ */}
      <Card className="shadow-none">
        <CardContent className="p-4">
          <SectionHeader icon={ShieldAlert} label="Issue Source & Trend Analysis" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Issues by Source */}
            <div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.12em] mb-2">By Source</p>
              {issueSourceData.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No issues</p>
              ) : (
              <div className="space-y-1.5">
                  {issueSourcePageData.items.map((s) => (
                    <div key={s.source} className="flex items-center gap-2">
                      <span className="text-xs w-24 truncate font-medium">{s.source}</span>
                      <div className="flex-1 h-4 rounded-full bg-secondary overflow-hidden flex">
                        {s.open > 0 && (
                          <div className="h-full bg-destructive/70" style={{ width: `${(s.open / s.total) * 100}%` }} />
                        )}
                        {s.resolved > 0 && (
                          <div className="h-full bg-success/70" style={{ width: `${(s.resolved / s.total) * 100}%` }} />
                        )}
                      </div>
                      <span className="text-[10px] font-bold w-5 text-right">{s.total}</span>
                    </div>
                  ))}
                  <ListPagination
                    page={issueSourcePageData.page}
                    totalPages={issueSourcePageData.totalPages}
                    onChange={(next) => setListPage("issueSourceData", next)}
                  />
                  <div className="flex items-center gap-3 mt-2 text-[9px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-destructive/70" /> Open</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-success/70" /> Resolved</span>
                  </div>
                </div>
              )}
            </div>

            {/* Issues by Crew / Sub */}
            <div>
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.12em] mb-2">By Crew / Subcontractor</p>
              {issuesByAssignee.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No issues</p>
              ) : (
                <div className="space-y-1.5">
                  {issuesByAssigneePageData.items.map((a) => (
                    <div key={a.name} className="flex items-center justify-between rounded-md border px-2.5 py-1.5 bg-muted/20">
                      <div className="flex items-center gap-1.5">
                        {a.type === "Internal Crew" ? <Shield className="h-3 w-3 text-primary" /> : <Users className="h-3 w-3 text-accent" />}
                        <span className="text-xs font-medium truncate max-w-[100px]">{a.name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px]">
                        {a.open > 0 && <span className="font-bold text-destructive">{a.open} open</span>}
                        {a.resolved > 0 && <span className="text-success">{a.resolved} resolved</span>}
                      </div>
                    </div>
                  ))}
                  <ListPagination
                    page={issuesByAssigneePageData.page}
                    totalPages={issuesByAssigneePageData.totalPages}
                    onChange={(next) => setListPage("issuesByAssignee", next)}
                  />
                </div>
              )}
            </div>

            {/* Issues by Project Type + Age */}
            <div className="space-y-4">
              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.12em] mb-2">By Project Type</p>
                {issuesByProjectType.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-4 text-center">No issues</p>
                ) : (
                  <div className="space-y-1">
                    {issuesByProjectTypePageData.items.map((p) => (
                      <div key={p.type} className="flex items-center justify-between text-xs">
                        <span className="truncate max-w-[140px] font-medium">{p.type}</span>
                        <div className="flex items-center gap-2 text-[10px]">
                          {p.open > 0 && <span className="text-destructive font-bold">{p.open}</span>}
                          {p.resolved > 0 && <span className="text-success">{p.resolved}</span>}
                        </div>
                      </div>
                    ))}
                    <ListPagination
                      page={issuesByProjectTypePageData.page}
                      totalPages={issuesByProjectTypePageData.totalPages}
                      onChange={(next) => setListPage("issuesByProjectType", next)}
                    />
                  </div>
                )}
              </div>
              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.12em] mb-2">Unresolved by Age</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {issueAgeData.map((b) => (
                    <div key={b.label} className={`rounded-md border text-center py-2 px-1 ${
                      b.count > 0 && b.min >= 14 ? "border-destructive/30 bg-destructive/5" :
                      b.count > 0 && b.min >= 7 ? "border-warning/30 bg-warning/5" :
                      "border-border bg-muted/20"
                    }`}>
                      <p className={`text-lg font-bold leading-none ${
                        b.count > 0 && b.min >= 14 ? "text-destructive" :
                        b.count > 0 && b.min >= 7 ? "text-warning" :
                        b.count > 0 ? "text-foreground" : "text-muted-foreground"
                      }`}>{b.count}</p>
                      <p className="text-[9px] text-muted-foreground mt-1 font-medium">{b.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ═══ CONTRACTOR PERFORMANCE SCORECARDS ═══ */}
      <ContractorScorecardTable
        scorecards={scorecardsPageData.items}
        page={scorecardsPageData.page}
        totalPages={scorecardsPageData.totalPages}
        onPageChange={(next) => setListPage("scorecards", next)}
      />

      {/* ═══ CREW UTILIZATION & CAPACITY ═══ */}
      {crewMetrics.length > 0 && (
        <Card className="shadow-none">
          <CardContent className="p-4">
            <SectionHeader icon={Users} label="Crew Utilization & Capacity" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              {[
                { label: "Overloaded", count: crewMetrics.filter((c) => c.status === "overloaded").length, color: "text-destructive", bg: "bg-destructive/10 border-destructive/20" },
                { label: "Optimal", count: crewMetrics.filter((c) => c.status === "optimal").length, color: "text-success", bg: "bg-success/10 border-success/20" },
                { label: "Light", count: crewMetrics.filter((c) => c.status === "underutilized").length, color: "text-warning", bg: "bg-warning/10 border-warning/20" },
                { label: "Idle", count: crewMetrics.filter((c) => c.status === "idle").length, color: "text-muted-foreground", bg: "bg-muted border-border" },
              ].map((s) => (
                <div key={s.label} className={`rounded-xl border p-3 text-center ${s.bg}`}>
                  <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-t bg-muted/40">
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Crew / Sub</th>
                    <th className="text-center py-2 px-3 font-medium text-muted-foreground">Today</th>
                    <th className="text-center py-2 px-3 font-medium text-muted-foreground">Week</th>
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground w-28">Utilization</th>
                    <th className="text-center py-2 px-3 font-medium text-muted-foreground">Active</th>
                    <th className="text-center py-2 px-3 font-medium text-muted-foreground">In Progress</th>
                    <th className="text-center py-2 px-3 font-medium text-muted-foreground">Done</th>
                    <th className="text-center py-2 px-3 font-medium text-muted-foreground">Issues</th>
                    <th className="text-center py-2 px-3 font-medium text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {crewMetricsPageData.items.map((c) => (
                    <tr key={c.name} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-1.5 px-3 font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {c.type === "Internal Crew" ? <Shield className="h-3 w-3 text-primary" /> : <Users className="h-3 w-3 text-accent" />}
                          {c.name}
                        </span>
                      </td>
                      <td className="py-1.5 px-3 text-center font-bold">{c.todayJobs}</td>
                      <td className="py-1.5 px-3 text-center font-bold">{c.weekJobs}</td>
                      <td className="py-1.5 px-3">
                        <div className="flex items-center gap-1.5">
                          <Progress
                            value={c.utilization}
                            className={`h-1.5 flex-1 ${
                              c.status === "overloaded" ? "[&>div]:bg-destructive" :
                              c.status === "optimal" ? "[&>div]:bg-success" :
                              c.status === "underutilized" ? "[&>div]:bg-warning" :
                              "[&>div]:bg-muted-foreground"
                            }`}
                          />
                          <span className="text-[10px] font-bold w-7 text-right">{c.utilization}%</span>
                        </div>
                      </td>
                      <td className="py-1.5 px-3 text-center">{c.totalAssigned}</td>
                      <td className="py-1.5 px-3 text-center">{c.inProgress}</td>
                      <td className="py-1.5 px-3 text-center text-success font-medium">{c.completed}</td>
                      <td className="py-1.5 px-3 text-center">
                        {c.openIssues > 0 ? <span className="text-destructive font-bold">{c.openIssues}</span> : <span className="text-muted-foreground">0</span>}
                      </td>
                      <td className="py-1.5 px-3 text-center">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
                          c.status === "overloaded" ? "bg-destructive/10 text-destructive border-destructive/20" :
                          c.status === "optimal" ? "bg-success/10 text-success border-success/20" :
                          c.status === "underutilized" ? "bg-warning/10 text-warning border-warning/20" :
                          "bg-muted text-muted-foreground border-border"
                        }`}>
                          {c.status === "overloaded" ? "Overloaded" : c.status === "optimal" ? "Optimal" : c.status === "underutilized" ? "Light" : "Idle"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ListPagination
                page={crewMetricsPageData.page}
                totalPages={crewMetricsPageData.totalPages}
                onChange={(next) => setListPage("crewMetrics", next)}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* ═══ ASSIGNMENT BREAKDOWN ═══ */}
      <Card className="shadow-none">
        <CardContent className="p-4">
          <SectionHeader icon={Users} label="Assignment Breakdown" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <AssignmentColumn
              title="Internal Crews"
              count={internalJobs.length}
              color="text-primary"
              jobs={internalJobsPageData.items}
              labelField="assignedCrew"
              page={internalJobsPageData.page}
              totalPages={internalJobsPageData.totalPages}
              onPageChange={(next) => setListPage("internalJobs", next)}
            />
            <AssignmentColumn
              title="Subcontractors"
              count={subJobs.length}
              color="text-accent"
              jobs={subJobsPageData.items}
              labelField="assignedSubcontractor"
              page={subJobsPageData.page}
              totalPages={subJobsPageData.totalPages}
              onPageChange={(next) => setListPage("subJobs", next)}
            />
            <AssignmentColumn
              title="Unassigned"
              count={unassignedJobs.length}
              color={unassignedJobs.length > 0 ? "text-warning" : "text-muted-foreground"}
              jobs={unassignedJobsPageData.items}
              labelField="currentStage"
              page={unassignedJobsPageData.page}
              totalPages={unassignedJobsPageData.totalPages}
              onPageChange={(next) => setListPage("unassignedJobs", next)}
            >
              <p className="text-xs text-success font-medium">All jobs assigned ✓</p>
            </AssignmentColumn>
          </div>
        </CardContent>
      </Card>

      {/* ═══ BLOCKER DETAIL TABLES ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="shadow-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-warning" />
              <CardTitle className="text-sm font-semibold">Permit Holds ({permitBlocked.length})</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {permitBlocked.length === 0 ? (
              <p className="text-xs text-success font-medium text-center py-4 px-4">No permit blocks ✓</p>
            ) : (
              <table className="w-full text-xs">
                <thead><tr className="border-b border-t bg-muted/40">
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Job</th>
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Customer</th>
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Status</th>
                </tr></thead>
                <tbody>
                  {permitBlockedPageData.items.map((j) => (
                    <tr key={j.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-1.5 px-4"><Link to={`/jobs/${j.id}`} className="text-primary font-medium hover:underline">{getJobDisplayRef(j)}</Link></td>
                      <td className="py-1.5 px-4 font-medium">{j.customerName}</td>
                      <td className="py-1.5 px-4"><StatusBadge type="permit" value={j.permitStatus} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <ListPagination
              page={permitBlockedPageData.page}
              totalPages={permitBlockedPageData.totalPages}
              onChange={(next) => setListPage("permitBlocked", next)}
            />
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-warning" />
              <CardTitle className="text-sm font-semibold">Materials Holds ({materialsBlocked.length})</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {materialsBlocked.length === 0 ? (
              <p className="text-xs text-success font-medium text-center py-4 px-4">No materials blocks ✓</p>
            ) : (
              <table className="w-full text-xs">
                <thead><tr className="border-b border-t bg-muted/40">
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Job</th>
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Customer</th>
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Materials</th>
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Order</th>
                </tr></thead>
                <tbody>
                  {materialsBlockedPageData.items.map((j) => (
                    <tr key={j.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-1.5 px-4"><Link to={`/jobs/${j.id}`} className="text-primary font-medium hover:underline">{getJobDisplayRef(j)}</Link></td>
                      <td className="py-1.5 px-4 font-medium">{j.customerName}</td>
                      <td className="py-1.5 px-4"><StatusBadge type="materialReadiness" value={j.materialReadiness} /></td>
                      <td className="py-1.5 px-4"><span className="text-[11px] font-semibold">{j.planningStatus || "Not Started"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <ListPagination
              page={materialsBlockedPageData.page}
              totalPages={materialsBlockedPageData.totalPages}
              onChange={(next) => setListPage("materialsBlocked", next)}
            />
          </CardContent>
        </Card>
      </div>

      {/* ═══ QA & CLOSEOUT ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="shadow-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-warning" />
              <CardTitle className="text-sm font-semibold">QA Review ({qaJobs.length})</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {qaJobs.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4 px-4">No jobs in QA review</p>
            ) : (
              <table className="w-full text-xs">
                <thead><tr className="border-b border-t bg-muted/40">
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Job</th>
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Customer</th>
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">QA</th>
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Assigned</th>
                </tr></thead>
                <tbody>
                  {qaJobsPageData.items.map((j) => (
                    <tr key={j.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-1.5 px-4"><Link to={`/jobs/${j.id}`} className="text-primary font-medium hover:underline">{getJobDisplayRef(j)}</Link></td>
                      <td className="py-1.5 px-4 font-medium">{j.customerName}</td>
                      <td className="py-1.5 px-4">
                        <span className={`text-[11px] font-semibold ${j.qaSubmitted ? "text-success" : "text-destructive"}`}>
                          {j.qaSubmitted ? "✓ Submitted" : "✗ Missing"}
                        </span>
                      </td>
                      <td className="py-1.5 px-4 text-muted-foreground">{j.assignedCrew || j.assignedSubcontractor || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <ListPagination
              page={qaJobsPageData.page}
              totalPages={qaJobsPageData.totalPages}
              onChange={(next) => setListPage("qaJobs", next)}
            />
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-success" />
              <CardTitle className="text-sm font-semibold">Closeout Needed ({closeoutJobs.length})</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {closeoutJobs.length === 0 ? (
              <p className="text-xs text-success font-medium text-center py-4 px-4">All completed jobs fully closed out ✓</p>
            ) : (
              <table className="w-full text-xs">
                <thead><tr className="border-b border-t bg-muted/40">
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Job</th>
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Customer</th>
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Remaining</th>
                </tr></thead>
                <tbody>
                  {closeoutJobsPageData.items.map((j) => {
                    const remaining: string[] = [];
                    const jobOpenIssues = issues.filter((i) => i.jobId === j.id && (i.status === "Open" || i.status === "In Progress"));
                    if (jobOpenIssues.length > 0) remaining.push(`${jobOpenIssues.length} open issue${jobOpenIssues.length > 1 ? "s" : ""}`);
                    if (!j.qaSubmitted) remaining.push("QA missing");
                    return (
                      <tr key={j.id} className="border-b last:border-0 hover:bg-muted/30">
<td className="py-1.5 px-4"><Link to={`/jobs/${j.id}`} className="text-primary font-medium hover:underline">{getJobDisplayRef(j)}</Link></td>
                        <td className="py-1.5 px-4 font-medium">{j.customerName}</td>
                        <td className="py-1.5 px-4">
                          <div className="flex flex-wrap gap-1">
                            {remaining.map((r) => (
                              <span key={r} className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-warning/10 text-warning border border-warning/20">{r}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <ListPagination
              page={closeoutJobsPageData.page}
              totalPages={closeoutJobsPageData.totalPages}
              onChange={(next) => setListPage("closeoutJobs", next)}
            />
          </CardContent>
        </Card>
      </div>

      {/* ═══ FINANCIAL & MARKETING PERFORMANCE ═══ */}
      {!fin$.viewFinancialSummary ? (
        <Card className="shadow-none">
          <CardContent className="p-6 text-center">
            <ShieldAlert className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm font-medium">Financial data restricted for your role</p>
            <p className="text-xs text-muted-foreground mt-1">Contact your administrator for access</p>
          </CardContent>
        </Card>
      ) : (
      <>
      <div className="flex items-center justify-between">
        <SectionHeader icon={DollarSign} label="Financial Performance" />
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-[11px] gap-1.5"
          onClick={() => {
            const headers = ["Job", "Customer", "Stage", "Contract Value", "Material Cost", "Labor Cost", "Sub Payout", "Est. Margin", "Margin %", "Payment Status", "Payout Status"];
            const rows = filteredJobs.map((j) => [
              getJobDisplayRef(j), j.customerName, j.currentStage,
              getJobDisplayValue(j),
              j.financials?.estimatedMaterialCost ?? 0,
              j.financials?.estimatedLaborCost ?? 0,
              j.financials?.subcontractorPayout ?? 0,
              j.financials?.marginEstimate ?? 0,
              j.financials && j.financials.contractValue > 0 ? ((j.financials.marginEstimate / j.financials.contractValue) * 100).toFixed(1) : "0",
              j.financials?.paymentStatus ?? "N/A",
              j.financials?.payoutStatus ?? "N/A",
            ]);
            downloadCSV("financial-report.csv", headers, rows);
          }}
        >
          <Download className="h-3 w-3" /> Export Financial CSV
        </Button>
      </div>

      {/* Financial KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total Revenue", value: `$${totalRevenue.toLocaleString()}`, color: "text-success" },
          { label: "Pipeline Value", value: `$${totalPipeline.toLocaleString()}`, color: "text-primary" },
          { label: "Est. Total Margin", value: `$${totalMargin.toLocaleString()}`, color: "text-info" },
          { label: "Avg Margin %", value: `${avgMarginPct}%`, color: avgMarginPct >= 30 ? "text-success" : avgMarginPct >= 15 ? "text-warning" : "text-destructive" },
        ].map((s) => (
          <Card key={s.label} className="shadow-none">
            <CardContent className="p-3 text-center">
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-medium">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Revenue over time + Margin trend */}
      {revenueOverTime.length > 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="shadow-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-success" /> Revenue Over Time
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-4">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={revenueOverTime}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(210,15%,90%)" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => `$${v.toLocaleString()}`} />
                  <Bar dataKey="revenue" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-info" /> Margin Trend
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-4">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={revenueOverTime}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(210,15%,90%)" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => `$${v.toLocaleString()}`} />
                  <Line type="monotone" dataKey="margin" stroke={CHART_COLORS.success} strokeWidth={2} dot={{ r: 4 }} name="Est. Margin" />
                  <Line type="monotone" dataKey="revenue" stroke={CHART_COLORS.primary} strokeWidth={2} dot={{ r: 4 }} name="Revenue" strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Revenue by Lead Source + Jobs by Lead Source */}
      <div className="flex items-center justify-between">
        <SectionHeader icon={Megaphone} label="Marketing Performance" />
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-[11px] gap-1.5"
          onClick={() => {
            const headers = ["Job", "Customer", "Lead Source", "Campaign", "Platform", "Revenue", "Margin", "Stage", "Sale Outcome"];
            const rows = filteredJobs.map((j) => [
              getJobDisplayRef(j), j.customerName,
              j.marketing?.leadSource ?? "Unknown",
              j.marketing?.campaignName ?? "",
              j.marketing?.aggregatorPlatform ?? "",
              getJobDisplayValue(j),
              j.financials?.marginEstimate ?? 0,
              j.currentStage,
              j.marketing?.saleOutcome ?? "",
            ]);
            downloadCSV("marketing-report.csv", headers, rows);
          }}
        >
          <Download className="h-3 w-3" /> Export Marketing CSV
        </Button>
      </div>

      {revenueBySource.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="shadow-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-success" /> Revenue by Lead Source
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-4">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={revenueBySource} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(210,15%,90%)" />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="source" tick={{ fontSize: 10 }} width={100} />
                  <Tooltip formatter={(v: number) => `$${v.toLocaleString()}`} />
                  <Bar dataKey="revenue" fill={CHART_COLORS.success} radius={[0, 4, 4, 0]} name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-primary" /> Jobs by Lead Source
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-4">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={revenueBySource} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(210,15%,90%)" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="source" tick={{ fontSize: 10 }} width={100} />
                  <Tooltip />
                  <Bar dataKey="jobs" fill={CHART_COLORS.primary} radius={[0, 4, 4, 0]} name="Jobs" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Campaign Performance Table */}
      {campaignPerf.length > 0 && (
        <Card className="shadow-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-accent" /> Campaign Performance
              </CardTitle>
              <Link to="/marketing-attribution" className="text-[10px] text-primary hover:underline inline-flex items-center gap-1">
                Full Marketing Report <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-t bg-muted/40">
                <th className="text-left py-2 px-4 font-medium text-muted-foreground">Campaign</th>
                <th className="text-center py-2 px-3 font-medium text-muted-foreground">Jobs</th>
                <th className="text-center py-2 px-3 font-medium text-muted-foreground">Closed</th>
                <th className="text-center py-2 px-3 font-medium text-muted-foreground">Close Rate</th>
                <th className="text-right py-2 px-4 font-medium text-muted-foreground">Revenue</th>
              </tr></thead>
              <tbody>
                {campaignPerfPageData.items.map((c) => (
                  <tr key={c.campaign} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-2 px-4 font-semibold">{c.campaign}</td>
                    <td className="py-2 px-3 text-center">{c.jobs}</td>
                    <td className="py-2 px-3 text-center text-success font-bold">{c.closed}</td>
                    <td className="py-2 px-3 text-center">
                      <span className={`font-bold ${c.closeRate >= 60 ? "text-success" : c.closeRate >= 30 ? "text-warning" : "text-destructive"}`}>
                        {c.closeRate}%
                      </span>
                    </td>
                    <td className="py-2 px-4 text-right font-medium">${c.revenue.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ListPagination
              page={campaignPerfPageData.page}
              totalPages={campaignPerfPageData.totalPages}
              onChange={(next) => setListPage("campaignPerf", next)}
            />
          </CardContent>
        </Card>
      )}

      {/* ═══════ Credits & Resolutions ═══════ */}
      {credits.length > 0 && (
        <Card className="shadow-none">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Receipt className="h-4 w-4 text-warning" /> Credits & Resolutions
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-[10px] gap-1 h-7" onClick={() => {
                const rows = credits.map((c) => [c.id, c.customerId, c.jobId, c.creditType, c.reasonCategory, c.attributionType, c.creditAmount, c.status, c.dateCreated, c.createdBy, c.issueId || "", c.relatedContractorId || "", c.relatedCrewName || ""]);
                downloadCSV("credits-report.csv", ["ID", "Customer", "Job", "Type", "Reason", "Attribution", "Amount", "Status", "Created", "Created By", "Issue", "Contractor", "Crew"], rows);
              }}>
                <Download className="h-3 w-3" /> Export
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Total Credits</p>
                <p className="text-lg font-bold text-primary">${credits.reduce((s, c) => s + c.creditAmount, 0).toLocaleString()}</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Records</p>
                <p className="text-lg font-bold">{credits.length}</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Applied</p>
                <p className="text-lg font-bold text-success">{credits.filter((c) => c.status === "Applied" || c.status === "Accepted").length}</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Pending</p>
                <p className="text-lg font-bold text-warning">{credits.filter((c) => c.status === "Draft" || c.status === "Proposed" || c.status === "Approved").length}</p>
              </div>
            </div>

            {/* By Credit Type */}
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-2">By Credit Type</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {creditsByTypePageData.items.map((entry) => (
                  <div key={entry.creditType} className="rounded-lg border p-2.5">
                    <p className="text-xs font-medium">{entry.creditType}</p>
                    <p className="text-sm font-bold text-primary">${entry.total.toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">{entry.count} record{entry.count !== 1 ? "s" : ""}</p>
                  </div>
                ))}
              </div>
              <ListPagination
                page={creditsByTypePageData.page}
                totalPages={creditsByTypePageData.totalPages}
                onChange={(next) => setListPage("creditsByType", next)}
              />
            </div>

            {/* By Customer */}
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-2">By Customer</p>
              <div className="space-y-1">
                {creditsByCustomerPageData.items.map((entry) => (
                  <div key={entry.customerId} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/30">
                    <Link to={`/customers/${encodeURIComponent(entry.customerId)}`} className="text-xs font-medium text-primary hover:underline">{entry.customerId}</Link>
                    <span className="text-xs font-bold">${entry.total.toLocaleString()} ({entry.count})</span>
                  </div>
                ))}
              </div>
              <ListPagination
                page={creditsByCustomerPageData.page}
                totalPages={creditsByCustomerPageData.totalPages}
                onChange={(next) => setListPage("creditsByCustomer", next)}
              />
            </div>

            {/* By Attribution Type */}
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-2">By Attribution</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {creditsByAttributionPageData.items.map((entry) => (
                  <div key={entry.attributionType} className="rounded-lg border p-2.5">
                    <p className="text-xs font-medium">{entry.attributionType}</p>
                    <p className="text-sm font-bold text-primary">${entry.total.toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">{entry.count} record{entry.count !== 1 ? "s" : ""}</p>
                  </div>
                ))}
              </div>
              <ListPagination
                page={creditsByAttributionPageData.page}
                totalPages={creditsByAttributionPageData.totalPages}
                onChange={(next) => setListPage("creditsByAttribution", next)}
              />
            </div>

            {/* Crew & Subcontractor Accountability */}
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-2">Crew & Subcontractor Accountability</p>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/40 border-b">
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Crew / Contractor</th>
                      <th className="text-center py-2 px-3 font-medium text-muted-foreground">Type</th>
                      <th className="text-center py-2 px-3 font-medium text-muted-foreground">Credits</th>
                      <th className="text-right py-2 px-3 font-medium text-muted-foreground">Total $</th>
                      <th className="text-right py-2 px-3 font-medium text-muted-foreground hidden sm:table-cell">Avg/Job</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground hidden md:table-cell">Patterns</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditsAccountabilityPageData.items.map((row) => (
                      <tr key={row.crewId ? `contractor-${row.crewId}` : `crew-${row.entity}`} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2 px-3 font-medium">{row.isContractor ? (
                          <Link to={`/contractors/${row.crewId}`} className="text-primary hover:underline">{row.entity}</Link>
                        ) : row.entity}</td>
                        <td className="py-2 px-3 text-center">
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${row.entityType === "Crew"
                            ? "bg-primary/10 text-primary border-primary/20"
                            : "bg-accent/10 text-accent border-accent/20"
                          }`}>
                            {row.entityType}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-center font-bold">{row.credits}</td>
                        <td className="py-2 px-3 text-right font-bold text-warning">${row.total.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-muted-foreground hidden sm:table-cell">${row.avgPerJob.toLocaleString()}</td>
                        <td className="py-2 px-3 hidden md:table-cell text-muted-foreground">{row.patterns}</td>
                      </tr>
                    ))}
                    {!credits.some((c) => c.relatedCrewName || c.relatedContractorId) && creditsAccountabilityPageData.totalPages === 1 && (
                      <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">No crew or contractor attributions yet</td></tr>
                    )}
                  </tbody>
                </table>
                <ListPagination
                  page={creditsAccountabilityPageData.page}
                  totalPages={creditsAccountabilityPageData.totalPages}
                  onChange={(next) => setListPage("creditsAccountability", next)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      </>
      )}
    </div>
  );
}

function AssignmentColumn({
  title,
  count,
  color,
  jobs: jobList,
  labelField,
  page,
  totalPages,
  onPageChange,
  children,
}: {
  title: string;
  count: number;
  color: string;
  jobs: Job[];
  labelField: string;
  page?: number;
  totalPages?: number;
  onPageChange?: (next: number) => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">{title}</p>
        <span className={`text-lg font-bold ${color}`}>{count}</span>
      </div>
      <div className="space-y-1">
        {jobList.length === 0 ? (
          children ?? <p className="text-xs text-muted-foreground">None</p>
        ) : (
          jobList.map((j) => (
            <div key={j.id} className="flex items-center justify-between text-xs">
              <Link to={`/jobs/${j.id}`} className="text-primary hover:underline font-medium">
                {getJobDisplayRef(j)}
              </Link>
              <span className="text-muted-foreground truncate ml-2">{j[labelField] || "—"}</span>
            </div>
          ))
        )}
        {typeof totalPages === "number" ? (
          <ListPagination
            page={page ?? 1}
            totalPages={totalPages}
            onChange={onPageChange ?? (() => undefined)}
          />
        ) : null}
      </div>
    </div>
  );
}
